import { Box, Button, Text, VStack } from "@chakra-ui/react"
import { FC, useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { BlobBackground } from "src/components/common/BlobBackground"
import { SafeArea } from "src/components/common/SafeArea"
import { PlansPageStyling } from "src/context/types"
import useAuthedUser from "src/hooks/useAuthedUser"
import { getConfigs } from "src/hooks/useConfig"
import { useOnboarding } from "src/hooks/useOnboarding"
import usePageStyling from "src/hooks/usePageStyling"
import useGridPageStyling from "../avatars/hooks/useGridPageStyling"
import {
  Board,
  Course,
  fetchBoards,
  fetchCourses,
  fetchLevels,
  Level
} from "./repo"
import StepSelector from "./StepSelector"

export type OnboardingStyleVars = {
  backgroundColor: string
  buttonColor: string
  buttonHoverColor: string
  textColor: string
}

export const Onboarding: FC = () => {
  const { getOnboarding, postOnboarding, putOnboarding } = useOnboarding()

  const { getAuthedUser } = useAuthedUser()
  const history = useHistory()

  const onboarding = getOnboarding()

  const { backgroundColor } = useGridPageStyling()
  const plansPageStyles: PlansPageStyling = usePageStyling("plansPageStyling")

  const styleVars = {
    backgroundColor: backgroundColor,
    buttonColor: plansPageStyles?.planPageButtonBackgroundColor,
    buttonHoverColor: plansPageStyles?.planPageButtonMouseoverBackgroundColor,
    textColor: plansPageStyles?.planPagePanelTextColor
  } satisfies OnboardingStyleVars

  const [step, setStep] = useState(0)
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null)
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null)
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null)

  const [courses, setCourses] = useState<Course[]>([])
  const [boards, setBoards] = useState<Board[]>([])
  const [levels, setLevels] = useState<Level[]>([])

  const [submitLoading, setSubmitLoading] = useState(false)

  useEffect(() => {
    async function loadData() {
      if (step === 0) {
        const fetchedCourses = await fetchCourses()
        setCourses(fetchedCourses)
      } else if (step === 1) {
        const fetchedBoards = await fetchBoards()
        setBoards(fetchedBoards)
      } else if (step === 2) {
        if (selectedBoard) {
          const fetchedLevels = await fetchLevels(selectedBoard)
          setLevels(fetchedLevels)
        } else {
          setStep(step - 1)
        }
      }
    }
    loadData()
  }, [step])

  const handleSelection = (value: Course | Board | Level) => {
    if (step === 0) setSelectedCourse(value as Course)
    if (step === 1) setSelectedBoard(value as Board)
    if (step === 2) setSelectedLevel(value as Level)
    setStep(step + 1)
  }

  const handleBack = () => {
    if (step > 0) setStep(step - 1)
  }

  const getCurrentOptions = () => {
    if (step === 0) return courses
    if (step === 1) return boards
    if (step === 2) return levels
    return []
  }

  const getStepTitle = () => {
    if (step === 0) return "Select a Course"
    if (step === 1) return "Select a Board"
    if (step === 2) return "Select a Level"
    return ""
  }

  const handleSubmit = async () => {
    setSubmitLoading(true)
    const authedUser = await getAuthedUser()

    // if the user has no onboarding lets create one
    if (onboarding && onboarding.length > 0) {
      await putOnboarding({
        ...onboarding[0],
        data: {
          selectedBoard: selectedBoard?.name,
          selectedCourse: selectedCourse?.name,
          selectedLevel: selectedLevel?.name
        }
      })
    } else {
      await postOnboarding({
        data: {
          selectedBoard: selectedBoard?.name,
          selectedCourse: selectedCourse?.name,
          selectedLevel: selectedLevel?.name
        },
        userId: authedUser.userId
      })
    }

    // redirect the user to post login redirect url
    const { postLoginRedirectUrl } = await getConfigs()
    setSubmitLoading(false)
    history.replace(postLoginRedirectUrl)
  }

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      background={styleVars.backgroundColor}
    >
      <BlobBackground
        background={
          plansPageStyles?.planPageBackgroundColor
            ? plansPageStyles.planPageBackgroundColor
            : "gray.100"
        }
      />
      <SafeArea position="absolute">
        <Box
          height="100%"
          display="flex"
          flexDir="column"
          justifyContent="center"
          alignItems="center"
        >
          {step < 3 ? (
            <StepSelector
              title={getStepTitle()}
              options={getCurrentOptions()}
              onSelect={handleSelection}
              onBack={handleBack}
              canGoBack={step > 0}
              buttonColor={styleVars.buttonColor}
              buttonHoverColor={styleVars.buttonHoverColor}
              styleVars={styleVars}
            />
          ) : (
            <VStack
              spacing={4}
              // divider={<StackDivider />}
              align="stretch"
              width="300px"
              padding={4}
              borderRadius="md"
              boxShadow="lg"
            >
              <Text
                textAlign={"center"}
                fontWeight={"700"}
                fontSize="3xl"
                color={styleVars.textColor}
              >
                Onboarding complete
              </Text>
              <Text
                fontSize="xl"
                fontWeight="500"
                textAlign={"center"}
                color={styleVars.textColor}
                whiteSpace="preserve"
              >{`You have selected:\n${selectedCourse?.name} - ${selectedBoard?.name} - ${selectedLevel?.name}`}</Text>

              <Button
                minHeight={"50px"}
                fontSize="lg"
                bg={styleVars.buttonColor}
                _hover={{ bg: styleVars.buttonHoverColor }}
                onClick={handleSubmit}
                isLoading={submitLoading}
              >
                Submit
              </Button>
              <Button
                minHeight={"50px"}
                fontSize="lg"
                bg={styleVars.buttonColor}
                _hover={{ bg: styleVars.buttonHoverColor }}
                onClick={handleBack}
              >
                Back
              </Button>
            </VStack>
          )}
        </Box>
      </SafeArea>
    </Box>
  )
}

export default Onboarding
